import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { formatDate } from '../../../utils/dateUtils';

const PreviewModal = ({ isOpen, onClose, content, settings, activeLanguage }) => {
  const [previewDevice, setPreviewDevice] = useState('desktop');

  if (!isOpen) return null;

  const deviceSizes = {
    desktop: { width: '100%', height: '100%' },
    tablet: { width: '768px', height: '1024px' },
    mobile: { width: '375px', height: '667px' }
  };

  const getPreviewContent = () => {
    const langContent = content?.[activeLanguage] || {};
    return {
      title: langContent?.title || 'Untitled Article',
      content: langContent?.content || '<p>No content available</p>',
      excerpt: langContent?.excerpt || ''
    };
  };

  const previewContent = getPreviewContent();
  const isRTL = activeLanguage === 'ar';

  const getCategoryLabel = (category) => {
    const categoryMap = {
      news: activeLanguage === 'ar' ? 'أخبار' : activeLanguage === 'tr' ? 'Haberler' : 'News',
      events: activeLanguage === 'ar' ? 'فعاليات' : activeLanguage === 'tr' ? 'Etkinlikler' : 'Events',
      announcements: activeLanguage === 'ar' ? 'إعلانات' : activeLanguage === 'tr' ? 'Duyurular' : 'Announcements',
      academic: activeLanguage === 'ar' ? 'أكاديمي' : activeLanguage === 'tr' ? 'Akademik' : 'Academic',
      cultural: activeLanguage === 'ar' ? 'ثقافي' : activeLanguage === 'tr' ? 'Kültürel' : 'Cultural',
      sports: activeLanguage === 'ar' ? 'رياضة' : activeLanguage === 'tr' ? 'Spor' : 'Sports'
    };
    return categoryMap?.[category] || category;
  };

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex flex-col h-full">
        {/* Preview Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-foreground font-arabic">
              Article Preview
            </h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Language:</span>
              <span className="text-sm font-medium text-foreground">
                {activeLanguage === 'ar' ? 'العربية' : activeLanguage === 'tr' ? 'Türkçe' : 'English'}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Device Toggle */}
            <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
              {[
                { key: 'desktop', icon: 'Monitor', label: 'Desktop' },
                { key: 'tablet', icon: 'Tablet', label: 'Tablet' },
                { key: 'mobile', icon: 'Smartphone', label: 'Mobile' }
              ]?.map((device) => (
                <Button
                  key={device?.key}
                  variant={previewDevice === device?.key ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8"
                  onClick={() => setPreviewDevice(device?.key)}
                  iconName={device?.icon}
                  iconSize={16}
                  title={device?.label}
                />
              ))}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
            >
              <span className="sr-only">Close preview</span>
            </Button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-muted p-4">
          <div 
            className="mx-auto bg-background shadow-elevation-2 transition-all duration-300"
            style={{
              width: deviceSizes?.[previewDevice]?.width,
              maxWidth: '100%',
              minHeight: deviceSizes?.[previewDevice]?.height
            }}
          >
            <div className={`p-6 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
              {/* Article Header */}
              <div className="mb-6">
                {settings?.category && (
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                      {getCategoryLabel(settings?.category)}
                    </span>
                  </div>
                )}

                <h1 className="text-3xl font-bold text-foreground mb-4 font-arabic">
                  {previewContent?.title}
                </h1>

                <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="User" size={16} />
                    <span>{settings?.author || 'Unknown Author'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} />
                    <span>{formatDate(new Date())}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>5 min read</span>
                  </div>
                </div>

                {previewContent?.excerpt && (
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {previewContent?.excerpt}
                  </p>
                )}
              </div>

              {/* Featured Image */}
              {settings?.featuredImage && (
                <div className="mb-6">
                  <Image
                    src={settings?.featuredImage}
                    alt="Featured article image showing main visual content"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Article Content */}
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: previewContent?.content }}
              />

              {/* Article Footer */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {settings?.tags && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Tag" size={16} className="text-muted-foreground" />
                        <div className="flex flex-wrap gap-2">
                          {settings?.tags?.split(',')?.map((tag, index) => (
                            <span 
                              key={index}
                              className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded"
                            >
                              {tag?.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={16} />
                      <span>{settings?.views || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="MessageCircle" size={16} />
                      <span>{settings?.comments || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Share2" size={16} />
                      <span>Share</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Footer */}
        <div className="p-4 border-t border-border bg-card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Preview Mode • {previewDevice?.charAt(0)?.toUpperCase() + previewDevice?.slice(1)} View
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
              >
                Close Preview
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                iconSize={16}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;